export type FrontMatter = {
  title?: string;
  description?: string;
  date?: string;
  tags?: string[];
};

export type ParsedMarkdown = {
  frontMatter: FrontMatter;
  body: string; // markdown body without front-matter
};

export function parseFrontMatter(raw: string): ParsedMarkdown {
  const trimmed = raw.trimStart();
  if (!trimmed.startsWith("---")) {
    return { frontMatter: {}, body: raw };
  }
  const end = trimmed.indexOf("\n---", 3);
  if (end === -1) return { frontMatter: {}, body: raw };
  const fmBlock = trimmed.slice(3, end).trim();
  const rest = trimmed.slice(end + 4).trimStart();
  const fm: FrontMatter = {};
  for (const line of fmBlock.split(/\r?\n/)) {
    const m = line.match(/^([a-zA-Z0-9_\-]+):\s*(.*)$/);
    if (!m) continue;
    const key = m[1];
    let value = m[2];
    // strip surrounding quotes if present
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    if (key === "tags") {
      // support [a, b, c] or comma-separated
      if (/^\[.*\]$/.test(value)) {
        const inner = value.slice(1, -1).trim();
        fm.tags = inner.length ? inner.split(/\s*,\s*/).map((s) => s.replace(/^['\"]|['\"]$/g, "")) : [];
      } else {
        fm.tags = value.split(/\s*,\s*/).filter(Boolean);
      }
    } else if (key === "title") fm.title = value;
    else if (key === "description") fm.description = value;
    else if (key === "date") fm.date = value;
  }
  return { frontMatter: fm, body: rest };
}

// Very small Markdown -> HTML (covers headings, paragraphs, code fences, inline code, lists, bold/italic, links)
export function markdownToHtml(md: string): string {
  // Escape HTML first
  let src = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Code fences ```
  src = src.replace(/```([\s\S]*?)```/g, (_, code) => `<pre><code>${code.replace(/\n$/,'')}</code></pre>`);

  // Headings
  src = src.replace(/^######\s+(.+)$/gm, '<h6>$1</h6>');
  src = src.replace(/^#####\s+(.+)$/gm, '<h5>$1</h5>');
  src = src.replace(/^####\s+(.+)$/gm, '<h4>$1</h4>');
  src = src.replace(/^###\s+(.+)$/gm, '<h3>$1</h3>');
  src = src.replace(/^##\s+(.+)$/gm, '<h2>$1</h2>');
  src = src.replace(/^#\s+(.+)$/gm, '<h1>$1</h1>');

  // Lists (- item)
  src = src.replace(/(^|\n)(?:-\s+.+(?:\n-\s+.+)*)/g, (block) => {
    const items = block
      .split(/\n/)
      .map((l) => l.replace(/^-/,'').trim())
      .filter(Boolean)
      .map((t) => `<li>${t}</li>`)                                    
      .join("");
    return `\n<ul>${items}</ul>`;
  });

  // Bold and italics
  src = src.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  src = src.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Inline code
  src = src.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Links [text](url)
  src = src.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Paragraphs: wrap remaining text blocks not already in tags
  src = src
    .split(/\n{2,}/)
    .map((chunk) => /^(<h\d|<ul|<pre|<p|<blockquote|<img|<!--)/.test(chunk.trim()) ? chunk : `<p>${chunk.trim()}</p>`)
    .join("\n");

  // Restore HTML comments (they were escaped)
  src = src.replace(/&lt;!--/g, '<!--').replace(/--&gt;/g, '-->');

  return src;
}


